import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabaseClient';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { AlertCircle, X } from 'lucide-react';

const AddSoftwareModal = ({ onClose, onSoftwareAdded }) => {
  const { t } = useTranslation();
  const { register, handleSubmit, control, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      total_seats: 1,
      annual_cost: 0
    }
  });
  const [error, setError] = useState('');

  const onSubmit = async (data) => {
    setError('');

    try {
      const { error: insertError } = await supabase
        .from('software_licenses')
        .insert([{
          name: data.name,
          vendor: data.vendor,
          total_seats: parseInt(data.total_seats),
          annual_cost: parseFloat(data.annual_cost),
          renewal_date: data.renewal_date || null,
          license_key: data.license_key || null
        }]);

      if (insertError) {
        throw insertError;
      }

      onSoftwareAdded(t('software_licenses.added_successfully'), 'success');
      onClose();

    } catch (err) {
      setError(err.message);
      console.error("Add software error:", err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-card rounded-xl border border-border shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{t('software_licenses.add_new_software')}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('software_licenses.register_new_subscription')}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-error/10 text-error p-3 rounded-lg flex items-center gap-2 border border-error/20">
              <AlertCircle size={20} />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          <div className="space-y-4">
            <Input
              label={t('software_licenses.software_name')}
              type="text"
              placeholder={t('software_licenses.software_name_placeholder')}
              {...register('name', { required: t('software_licenses.software_name_required') })}
              error={errors.name?.message}
            />

            <Input
              label={t('software_licenses.vendor')}
              type="text"
              placeholder={t('software_licenses.vendor_placeholder')}
              {...register('vendor', { required: t('software_licenses.vendor_required') })}
              error={errors.vendor?.message}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('software_licenses.total_seats')}
                type="number"
                placeholder="1"
                {...register('total_seats', { 
                  required: t('software_licenses.total_seats_required'),
                  min: { value: 0, message: t('software_licenses.cannot_be_negative') }
                })}
                error={errors.total_seats?.message}
              />

              <Input
                label={t('software_licenses.annual_cost_label')}
                type="number"
                step="0.01"
                placeholder="0.00"
                {...register('annual_cost', { 
                  required: t('software_licenses.annual_cost_required'),
                  min: { value: 0, message: t('software_licenses.cannot_be_negative') }
                })}
                error={errors.annual_cost?.message}
              />
            </div>

            <Input
              label={t('software_licenses.renewal_date')}
              type="date"
              {...register('renewal_date')}
              error={errors.renewal_date?.message}
            />

            <Input
              label={t('software_licenses.license_key_optional')}
              type="text"
              placeholder={t('software_licenses.license_key_placeholder')}
              {...register('license_key')}
            />
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-border">
            <Button variant="outline" type="button" onClick={onClose} disabled={isSubmitting}>
              {t('software_licenses.cancel')}
            </Button>
            <Button 
              type="submit" 
              loading={isSubmitting} 
              disabled={isSubmitting}
            >
              {isSubmitting ? t('software_licenses.saving') : t('software_licenses.add_software')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSoftwareModal;
